import React, {useCallback, useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap';
import {FaCheck, FaDownload, FaEdit, FaRobot, FaSearch, FaTrash} from 'react-icons/fa';
import * as api from '../api';
import {useTranslation} from 'react-i18next';
import RepMonitoringPanel from './rep-monitoring-panel';
import RepFeatureControl from './rep-feature-control';

const categoryKeys = {
  unrecognized: 'REP_FEEDBACK_UNRECOGNIZED',
  unsupported: 'REP_FEEDBACK_UNSUPPORTED',
  technical_error: 'REP_FEEDBACK_TECHNICAL_ERROR'
};
const statusKeys = {new: 'NEW', reviewing: 'REP_FEEDBACK_REVIEWING', draft: 'DRAFT', approved: 'APPROVED', ignored: 'REP_FEEDBACK_IGNORED'};
const emptyFilters = {q: '', category: '', status: '', page: 1, perPage: 25};

const editorFromItem = item => ({
  category: item.category,
  status: item.status,
  suggestedAnswer: item.suggestedAnswer || '',
  approvedAnswer: item.approvedAnswer || '',
  synonymsText: (item.synonyms || []).join(', '),
  targetRoute: item.targetRoute || '',
  targetAction: item.targetAction || '',
  resolutionNotes: item.resolutionNotes || ''
});

const AdminRepLearning = ({token}) => {
  const {t} = useTranslation();
  const categoryLabel = category => categoryKeys[category] ? t(categoryKeys[category]) : category;
  const statusLabel = status => statusKeys[status] ? t(statusKeys[status]) : status;
  const roleName = role => ({0: t('USER'), 1: t('PARTNER'), 2: t('ADMIN')}[role] || t('ROLE_NUMBER', {role}));
  const [filters, setFilters] = useState(emptyFilters);
  const [searchInput, setSearchInput] = useState('');
  const [result, setResult] = useState({items: [], total: 0, pages: 0, summary: {}});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [featureSaving, setFeatureSaving] = useState(false);

  const loadFeatureSetting = useCallback(() => {
    if (!token) return;
    setFeatureLoading(true);
    api.adminGetRepSettings(token).then(response => {
      setFeatureLoading(false);
      if (response.status === 200 && typeof response.json.enabled === 'boolean') {
        setFeatureEnabled(response.json.enabled);
        return;
      }
      setMessage({variant: 'danger', text: t('REP_FEATURE_LOAD_ERROR')});
    });
  }, [t, token]);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    api.adminGetRepFeedback(filters, token).then(response => {
      setLoading(false);
      if (response.status >= 200 && response.status < 300) setResult(response.json);
      else setMessage({variant: 'danger', text: t('REP_FEEDBACK_LOAD_ERROR')});
    });
  }, [filters, token, t]);

  useEffect(load, [load]);
  useEffect(loadFeatureSetting, [loadFeatureSetting]);

  const toggleFeature = async enabled => {
    if (!enabled && !window.confirm(t('REP_FEATURE_DISABLE_CONFIRM'))) return;
    setFeatureSaving(true);
    setMessage(null);
    const response = await api.adminUpdateRepSettings(enabled, token);
    setFeatureSaving(false);
    if (response.status === 200 && typeof response.json.enabled === 'boolean') {
      setFeatureEnabled(response.json.enabled);
      setMessage({variant: 'success', text: t(response.json.enabled ? 'REP_FEATURE_ENABLED_MESSAGE' : 'REP_FEATURE_DISABLED_MESSAGE')});
      window.dispatchEvent(new CustomEvent('replayer:rep-feature-changed', {detail: {enabled: response.json.enabled}}));
      return;
    }
    setMessage({variant: 'danger', text: t('REP_FEATURE_SAVE_ERROR')});
  };

  const openEditor = item => {
    setSelected(item);
    setEditor(editorFromItem(item));
    setMessage(null);
  };
  const updateEditor = field => event => setEditor(current => ({...current, [field]: event.target.value}));
  const payload = status => ({
    ...editor,
    status: status || editor.status,
    synonyms: editor.synonymsText.split(',').map(value => value.trim()).filter(Boolean)
  });
  const save = status => {
    setSaving(true);
    api.adminUpdateRepFeedback(selected.id, payload(status), token).then(response => {
      setSaving(false);
      if (response.status >= 200 && response.status < 300) {
        setSelected(null);
        setMessage({variant: 'success', text: status === 'approved' ? t('REP_FEEDBACK_APPROVED_MESSAGE') : t('REP_FEEDBACK_REVIEW_SAVED')});
        load();
      } else setMessage({variant: 'danger', text: t('REP_FEEDBACK_APPROVAL_REQUIREMENTS')});
    });
  };
  const remove = () => {
    if (!window.confirm(t('REP_FEEDBACK_CONFIRM_DELETE'))) return;
    api.adminDeleteRepFeedback(selected.id, token).then(response => {
      if (response.status >= 200 && response.status < 300) {
        setSelected(null);
        load();
      }
    });
  };

  return <main className="admin-rep-learning container">
    <header className="admin-rep-learning__hero"><span><FaRobot/> {t('REP_BACKOFFICE')}</span><h1>{t('IMPROVE_REP')}</h1><p>{t('IMPROVE_REP_INTRO')}</p></header>
    <RepFeatureControl enabled={featureEnabled} loading={featureLoading} saving={featureSaving} onToggle={toggleFeature}/>
    <RepMonitoringPanel token={token}/>
    <aside className="admin-rep-learning__notice"><strong>{t('REP_NO_AUTOMATIC_PRODUCTION_CHANGES')}</strong><span>{t('REP_APPROVED_CHANGES_NOTICE')}</span></aside>
    {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
    <section className="admin-rep-learning__summary">
      {Object.keys(statusKeys).map(key => <button key={key} type="button" onClick={() => setFilters(current => ({...current, status: key, page: 1}))}><span>{statusLabel(key)}</span><strong>{(result.summary || {})[key] || 0}</strong></button>)}
    </section>
    <Form className="admin-rep-learning__filters" onSubmit={event => { event.preventDefault(); setFilters(current => ({...current, q: searchInput.trim(), page: 1})); }}>
      <label><span>{t('SEARCH_QUESTIONS')}</span><div><FaSearch/><input value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder={t('REP_SEARCH_EXAMPLE')}/><button type="submit">{t('SEARCH')}</button></div></label>
      <label><span>{t('TYPE')}</span><select value={filters.category} onChange={event => setFilters(current => ({...current, category: event.target.value, page: 1}))}><option value="">{t('ALL')}</option>{Object.keys(categoryKeys).map(key => <option key={key} value={key}>{categoryLabel(key)}</option>)}</select></label>
      <label><span>{t('STATE')}</span><select value={filters.status} onChange={event => setFilters(current => ({...current, status: event.target.value, page: 1}))}><option value="">{t('ALL')}</option>{Object.keys(statusKeys).map(key => <option key={key} value={key}>{statusLabel(key)}</option>)}</select></label>
      <Button variant="outline-primary" onClick={() => api.adminExportRepFeedback(filters, token)}><FaDownload/> {t('EXPORT_CSV')}</Button>
    </Form>
    <section className="admin-rep-learning__list" aria-busy={loading}>
      <div className="admin-rep-learning__list-heading"><h2>{t('GROUPED_REPORTS')}</h2><span>{t('GROUP_COUNT', {count: result.total || 0})}</span></div>
      {loading ? <div className="admin-rep-learning__loading"><Spinner animation="border"/><span>{t('LOADING_REQUESTS')}</span></div> : null}
      {!loading && !(result.items || []).length ? <p className="admin-rep-learning__empty">{t('NO_REQUESTS_FOR_FILTERS')}</p> : null}
      {(result.items || []).map(item => <article key={item.id}>
        <div><span className={`is-${item.category}`}>{categoryLabel(item.category)}</span><small>{statusLabel(item.status)}</small></div>
        <h3>{item.question}</h3>
        <p>{item.attemptedIntent ? t('ATTEMPTED_INTERPRETATION', {intent: item.attemptedIntent}) : t('NO_INTERPRETATION_RECOGNIZED')}</p>
        <dl><div><dt>{t('FREQUENCY')}</dt><dd>{item.occurrenceCount}</dd></div><div><dt>{t('ROLES')}</dt><dd>{(item.rolesSeen || []).map(roleName).join(', ') || '—'}</dd></div><div><dt>{t('SOURCE')}</dt><dd>{(item.sourcePathsSeen || []).join(', ') || '—'}</dd></div></dl>
        <Button onClick={() => openEditor(item)}><FaEdit/> {t('MANAGE')}</Button>
      </article>)}
    </section>
    <div className="admin-rep-learning__pagination"><Button disabled={filters.page <= 1} onClick={() => setFilters(current => ({...current, page: current.page - 1}))}>{t('BACK')}</Button><span>{t('PAGE_OF', {page: filters.page, pages: result.pages || 1})}</span><Button disabled={filters.page >= (result.pages || 1)} onClick={() => setFilters(current => ({...current, page: current.page + 1}))}>{t('NEXT')}</Button></div>
    <Modal show={Boolean(selected && editor)} onHide={() => setSelected(null)} size="lg" centered className="admin-rep-learning-modal">
      {selected && editor ? <><Modal.Header closeButton><Modal.Title>{t('REP_KNOWLEDGE_REVIEW')}</Modal.Title></Modal.Header><Modal.Body>
        <div className="admin-rep-learning-modal__question"><span>{t('COLLECTED_QUESTION')}</span><strong>{selected.question}</strong><small>{t('SIMILAR_REQUEST_COUNT', {count: selected.occurrenceCount})}</small></div>
        <Form.Row><Form.Group className="col-md-6"><Form.Label>{t('CATEGORY')}</Form.Label><Form.Control as="select" value={editor.category} onChange={updateEditor('category')}>{Object.keys(categoryKeys).map(key => <option key={key} value={key}>{categoryLabel(key)}</option>)}</Form.Control></Form.Group><Form.Group className="col-md-6"><Form.Label>{t('REVIEW_STATUS')}</Form.Label><Form.Control as="select" value={editor.status} onChange={updateEditor('status')}>{Object.keys(statusKeys).map(key => <option key={key} value={key}>{statusLabel(key)}</option>)}</Form.Control></Form.Group></Form.Row>
        <Form.Group><Form.Label>{t('APPROVED_CORRECT_ANSWER')}</Form.Label><Form.Control as="textarea" rows={3} value={editor.approvedAnswer} onChange={updateEditor('approvedAnswer')} placeholder={t('APPROVED_ANSWER_PLACEHOLDER')}/></Form.Group>
        <Form.Group><Form.Label>{t('SYNONYMS')}</Form.Label><Form.Control value={editor.synonymsText} onChange={updateEditor('synonymsText')} placeholder={t('SYNONYMS_EXAMPLE')}/><Form.Text>{t('SEPARATE_WITH_COMMA')}</Form.Text></Form.Group>
        <Form.Row><Form.Group className="col-md-7"><Form.Label>{t('LINKED_PAGE')}</Form.Label><Form.Control value={editor.targetRoute} onChange={updateEditor('targetRoute')} placeholder="/player-video?section=highlight"/></Form.Group><Form.Group className="col-md-5"><Form.Label>{t('FUNCTION')}</Form.Label><Form.Control value={editor.targetAction} onChange={updateEditor('targetAction')} placeholder="open_highlight"/></Form.Group></Form.Row>
        <Form.Group><Form.Label>{t('INTERNAL_NOTES')}</Form.Label><Form.Control as="textarea" rows={2} value={editor.resolutionNotes} onChange={updateEditor('resolutionNotes')}/></Form.Group>
        <Alert variant="info">{t('REP_APPROVAL_DEPLOY_NOTICE')}</Alert>
      </Modal.Body><Modal.Footer><Button variant="outline-danger" onClick={remove}><FaTrash/> {t('DELETE')}</Button><Button variant="outline-secondary" onClick={() => save('ignored')}>{t('IGNORE')}</Button><Button variant="outline-primary" onClick={() => save('draft')} disabled={saving}>{t('SAVE_DRAFT')}</Button><Button variant="primary" onClick={() => save('approved')} disabled={saving}><FaCheck/> {t('APPROVE_FOR_RELEASE')}</Button></Modal.Footer></> : null}
    </Modal>
  </main>;
};

export default connect(({login: {token}}) => ({token}))(AdminRepLearning);
